import * as React from 'react';

import classnames from 'classnames';

import {AddOnPicker} from 'shells/billing/add-ons/add-on-picker';
import {GridListLoadingCards} from 'shells/grid-list/grid-list-loading-cards';

import {ErrorState} from '../../error-state/error-state';
import {Illustration} from '../../illustrations/illustration';
import {Body} from '../../typography/body';
import {useResizeObserver} from '../../utils/hooks/use-resize-observer';

import type {Plan, AddOns, SelectedAddOn, Product} from '../types';

import {PlanPickerCard} from './plan-picker-card';

import styles from './plan-picker.module.css';

type Props = {
    plans: Plan[];
    isLoading: boolean;
    errorMessage: string | null | undefined;
    selectedPlanCode: string | null | undefined;
    onSelectedPlanCodeChange: (planCode: string) => void;
    variant?: 'default' | 'engagement' | 'marketing';
    addOns?: AddOns | null | undefined;
    selectedAddOns?: SelectedAddOn[];
    setSelectedAddOns?: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void;
    onSelectedPlanChange?: (
        product: Product,
        planCode: string,
        setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void,
        quantity?: number
    ) => void;
    onPlanIsEnabledChange?: (
        product: Product,
        planCode: string,
        setSelectedAddOns: (arg1: (prevSelectedAddOns: SelectedAddOn[]) => SelectedAddOn[]) => void,
        quantity?: number | null | undefined,
        keepEnabled?: boolean
    ) => void;
    onOpenModal?: () => void;
};

export function PlanPicker(props: Props) {
    const {variant = 'default'} = props;
    const containerRef = React.useRef<HTMLDivElement | null | undefined>(null);
    const [width] = useResizeObserver(containerRef.current);

    return (
        <div
            // @ts-expect-error
            ref={containerRef}
            // @ts-expect-error
            style={{marginBottom: (variant === 'engagement' || variant === 'marketing') && '16px'}}
            className={styles.container}
        >
            {props.isLoading && width && (
                <div className='py-16'>
                    <GridListLoadingCards
                        key='loading-cards'
                        numCards={4}
                        cardHeight={800}
                        cardWidth={(width - 48) / 4}
                        flexProps={{gap: 16}}
                    />
                </div>
            )}
            {props.errorMessage && <ErrorState errorMessage={props.errorMessage} />}
            {!props.isLoading && !props.errorMessage && (
                <div
                    className={classnames(
                        'flex',
                        'gap-16',
                        'py-16',
                        variant === 'engagement' || variant === 'marketing'
                            ? 'justify-start'
                            : 'justify-sb'
                    )}
                >
                    {props.plans.map((plan) => {
                        return (
                            <PlanPickerCard
                                variant={variant}
                                key={plan.code}
                                plan={plan}
                                isSelected={plan.code === props.selectedPlanCode}
                                onIsSelectedChange={props.onSelectedPlanCodeChange}
                            />
                        );
                    })}
                    <div className={styles['right-side-container']}>
                        {props.addOns &&
                            props.setSelectedAddOns &&
                            props.onSelectedPlanChange &&
                            props.onPlanIsEnabledChange &&
                            props.onOpenModal && (
                                <div className={styles['marketing-container']}>
                                    <div className='mb-32'>
                                        <Body isBold={true} as='div'>
                                            Send beautifully designed emails to thousands of people
                                            at once
                                        </Body>
                                    </div>
                                    <AddOnPicker
                                        addOns={props.addOns}
                                        selectedAddOns={props.selectedAddOns || []}
                                        setSelectedAddOns={props.setSelectedAddOns}
                                        onSelectedPlanChange={props.onSelectedPlanChange}
                                        onPlanIsEnabledChange={props.onPlanIsEnabledChange}
                                        onOpenModal={props.onOpenModal}
                                        cardWidth='700px'
                                        route='marketing'
                                    />
                                </div>
                            )}

                        {variant === 'engagement' && (
                            <Illustration src='headset-chatting-bubble' width={450} />
                        )}
                        {variant === 'marketing' && (
                            <Illustration src='marketing-plan-stars' width={450} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
